import { memo, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch, useFieldArray } from 'react-hook-form';
import { AgendaViewsWrapper, KpiWrapper, ModuleWidgetWrapper } from './DashboardRowContent.styles';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';

import { modules } from 'components/user/Overview/mockData';
import { ModuleWidget } from 'components/user/Overview/Widgets/ModuleWidget';
import { KpiItem } from '../item/kpiItem/KpiItem';
import { AgendaViewItem } from '../item/agendaViewItem/AgendaViewItem';
import { SimpleWidgetCard } from 'components/user/Overview/Widgets/InfoWidgets/SimpleWidgetCard';
import type { AgendaViewItem as AgendaViewItemType, KpiItem as KpiItemType } from 'types/dashboard';
import type { DashboardItem } from 'types';

interface Props {
  type: 'kpiItems' | 'agendaViewItems' | 'modules';
  index: number;
  onRemove: (i: number) => void;
  onChange: (i: number, data: object) => void;
  isHelios?: boolean;
}

const DashboardRowContentBase = ({ type, index, onRemove, onChange, isHelios = false }: Props) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext();

  const viewMode = useWatch({ name: `dashboard.${index}.viewMode`, control });

  const { fields, move } = useFieldArray({
    control,
    name: `dashboard.${index}.items`,
  });

  const watchedItems = useWatch({
    control,
    name: `dashboard.${index}.items`,
  }) as DashboardItem['items'];

  const items = useMemo(() => {
    return fields.map((field, i) => {
      const freshData = watchedItems?.[i];
      return {
        ...field,
        ...freshData,
      } as (KpiItemType | AgendaViewItemType) & { id: string };
    });
  }, [fields, watchedItems]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        move(oldIndex, newIndex);

        const currentItems = getValues(`dashboard.${index}.items`) as DashboardItem['items'];
        if (!currentItems) return;

        const reordered = currentItems.map((item, idx: number) => ({
          ...item,
          order: idx,
        }));
        setValue(`dashboard.${index}.items`, reordered, { shouldDirty: true });
      }
    },
    [items, move, index, getValues, setValue]
  );

  const getContent = useMemo(() => {
    if (type === 'modules') {
      return (
        <ModuleWidgetWrapper>
          {viewMode === 'agenda'
            ? Array(4)
                .fill(null)
                .map((_, i: number) => <SimpleWidgetCard key={i} name="" />)
            : modules.map((module, i) => <ModuleWidget mock key={i} {...module} />)}
        </ModuleWidgetWrapper>
      );
    }

    if (type === 'kpiItems') {
      if (!items?.length) return <div>{t('core.settings.dashboard.noKPI')}</div>;

      const kpiFields = items as unknown as (KpiItemType & { id: string })[];

      return (
        <KpiWrapper count={items.length}>
          {kpiFields
            .toSorted((a, b) => a.order - b.order)
            .map((kpi, i: number) => (
              <KpiItem
                key={kpi.id}
                kpiItemData={kpi}
                isHeliosModule={isHelios}
                removeItem={() => onRemove(i)}
                setKpiData={(data) => onChange(i, data)}
              />
            ))}
        </KpiWrapper>
      );
    }

    if (type === 'agendaViewItems') {
      if (!items?.length) return <div>{t('core.settings.dashboard.noAgendas')}</div>;

      const agendaFields = items as unknown as (AgendaViewItemType & { id: string })[];

      return (
        <AgendaViewsWrapper>
          {agendaFields
            .toSorted((a, b) => a.order - b.order)
            .map((agenda, i: number) => (
              <AgendaViewItem key={agenda.id} agenda={agenda} removeItem={() => onRemove(i)} setAgendaViewData={(data) => onChange(i, data)} />
            ))}
        </AgendaViewsWrapper>
      );
    }

    return null;
  }, [type, viewMode, items, t, isHelios, onRemove, onChange]);

  if (type === 'modules') return getContent;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((item) => item.id)} strategy={rectSortingStrategy}>
        {getContent}
      </SortableContext>
    </DndContext>
  );
};

export const DashboardRowContent = memo(DashboardRowContentBase) as typeof DashboardRowContentBase;
